import React, { useRef } from 'react';
import { Cross2Icon, PaperPlaneIcon, UploadIcon } from '@radix-ui/react-icons';
import { KanbanComment, PendingCommentAttachment } from '../types';
import { formatRelativeTime } from '../constants';

interface TaskCommentsProps {
  comments: KanbanComment[];
  newComment: string;
  onNewCommentChange: (value: string) => void;
  onSend: () => void;
  onAttachFiles?: (files: FileList) => void;
  pendingAttachments?: PendingCommentAttachment[];
  onRemovePendingAttachment?: (localId: string) => void;
  isSending?: boolean;
  currentUserId?: string | null;
  onDeleteComment?: (commentId: string) => void;
}

export const TaskComments: React.FC<TaskCommentsProps> = ({
  comments,
  newComment,
  onNewCommentChange,
  onSend,
  onAttachFiles,
  pendingAttachments = [],
  onRemovePendingAttachment,
  isSending = false,
  currentUserId,
  onDeleteComment,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const displayComments = [...comments].reverse();
  const isUploading = pendingAttachments.some((attachment) => attachment.isUploading);

  return (
    <div className="task-details-modal__comments">
      <p className="task-details-modal__comments-count">
        {comments.length} Comment{comments.length === 1 ? '' : 's'}
      </p>

      <ul className="task-details-modal__comment-list">
        {displayComments.map((comment) => {
          const isOwner =
            currentUserId != null && comment.authorId === String(currentUserId);
          return (
            <li key={comment.id} className="task-details-modal__comment">
              <div className="task-details-modal__comment-head">
                <strong>{comment.author}</strong>
                <span>{formatRelativeTime(comment.createdAt)}</span>
                {isOwner && onDeleteComment && (
                  <button
                    type="button"
                    className="task-details-modal__comment-delete"
                    onClick={() => onDeleteComment(comment.id)}
                  >
                    Delete
                  </button>
                )}
              </div>
              <p>{comment.text}</p>
              {comment.attachments.length > 0 && (
                <ul className="task-details-modal__comment-files">
                  {comment.attachments.map((file) => (
                    <li key={file.fileId}>
                      <a href={file.url} target="_blank" rel="noreferrer">
                        {file.fileName}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {pendingAttachments.length > 0 && (
        <ul className="task-details-modal__pending-files" aria-label="Attached files">
          {pendingAttachments.map((attachment) => (
            <li key={attachment.localId} className="task-details-modal__pending-file">
              {attachment.url ? (
                <a href={attachment.url} target="_blank" rel="noreferrer">
                  {attachment.fileName}
                </a>
              ) : (
                <span>{attachment.fileName}</span>
              )}
              {attachment.isUploading && (
                <span className="task-details-modal__pending-file-status">Uploading…</span>
              )}
              {onRemovePendingAttachment && !isSending && (
                <button
                  type="button"
                  className="task-details-modal__pending-file-remove"
                  aria-label={`Remove ${attachment.fileName}`}
                  onClick={() => onRemovePendingAttachment(attachment.localId)}
                  disabled={attachment.isUploading}
                >
                  <Cross2Icon aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="task-details-modal__comment-compose">
        <input
          type="text"
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => onNewCommentChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isSending && !isUploading) onSend();
          }}
          disabled={isSending}
        />
        <div className="task-details-modal__comment-actions">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length && onAttachFiles) {
                onAttachFiles(e.target.files);
                e.target.value = '';
              }
            }}
          />
          <button
            type="button"
            aria-label="Attach file"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending}
          >
            <UploadIcon />
          </button>
          <button
            type="button"
            className="task-details-modal__send"
            onClick={onSend}
            aria-label="Send comment"
            disabled={isSending || isUploading || !newComment.trim()}
          >
            <PaperPlaneIcon />
          </button>
        </div>
      </div>
    </div>
  );
};
